import React, { useMemo, useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { formatNumber, getSortIndicator } from '../../../../utils/helpers';
import useSortedData from '../../hooks/useSortedData';
import './EntrantsTeamsPokerRunning.css';

// Adding a simple refresh icon (Unicode character)
const REFRESH_ICON = '\u21BB'; // Clockwise arrow
const PAUSE_ICON = '\u25A0'; // Black square
const PLAY_ICON = '\u25B6'; // Play symbol

const EntrantsTeamsPokerRunning = ({ 
  contest, 
  currentUsername, 
  onTeamSelect, 
  onRefreshData,
  isAutoRefreshEnabled,
  onToggleAutoRefresh,
  refreshError 
}) => {
  // Add countdown state
  const [countdown, setCountdown] = useState(60);

  // Reset countdown when auto-refresh is toggled
  useEffect(() => {
    if (isAutoRefreshEnabled) {
      setCountdown(60);
    }
  }, [isAutoRefreshEnabled]);

  // Handle countdown and refresh
  useEffect(() => {
    let timer;
    if (isAutoRefreshEnabled) {
      if (countdown > 0) {
        timer = setInterval(() => {
          setCountdown(prev => prev - 1);
        }, 1000);
      } else {
        // When countdown hits 0, trigger refresh and reset countdown
        onRefreshData();
        setCountdown(60);
      }
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isAutoRefreshEnabled, countdown, onRefreshData]);

  // Create a map of player names to their relevant data
  const playerDataMap = useMemo(() => {
    const map = {};
    contest.players.forEach(player => {
      map[player.name] = { 
        finish: player.finish, // Keep finish for max cash calc? (Check usage)
        cashedFor: player.cashedFor, 
        currentChipCount: player.sportSpecificData?.currentChipCount // Add chip count
      };
    });
    return map;
  }, [contest.players]);

  // Helper to get top N prizes from sorted payouts (memoized)
  const getTopPrizes = useCallback((numPrizes) => {
    const payouts = contest.playerPayouts || [];
    if (payouts.length === 0) return [];
    if (typeof payouts[0] === 'object' && payouts[0] !== null && 'prize' in payouts[0]) {
      return payouts.slice(0, numPrizes).map(p => p.prize);
    } else if (typeof payouts[0] === 'number') {
      return payouts.slice(0, numPrizes);
    }
    console.warn("Unexpected playerPayouts format:", payouts);
    return [];
  }, [contest.playerPayouts]); // Dependency: contest.playerPayouts

  // Calculate maximum possible cashes (memoized)
  const calculateMaxCashes = useCallback((team) => {
    const remainingPlayers = [];
    let currentCashes = 0;

    // Process Captain
    const captainData = playerDataMap[team.captain];
    const captainChips = captainData?.currentChipCount;
    // Check if captain is remaining based on chips
    if (captainChips > 0 || captainChips === null) { 
      remainingPlayers.push(team.captain);
    } else {
      // Captain is eliminated, add their actual cashed amount
      currentCashes += captainData?.cashedFor || 0; 
    }

    // Process Flex Players
    team.flexPlayers.forEach(player => {
      const playerData = playerDataMap[player];
      const playerChips = playerData?.currentChipCount;
      // Check if player is remaining based on chips
      if (playerChips > 0 || playerChips === null) { 
        remainingPlayers.push(player);
      } else {
        // Player is eliminated, add their actual cashed amount
        currentCashes += playerData?.cashedFor || 0; 
      }
    });

    let potentialTopWinnings = 0;
    const numRemaining = remainingPlayers.length;
    if (numRemaining > 0) {
      const topPrizes = getTopPrizes(numRemaining); // Use memoized getTopPrizes
      if (topPrizes.length > 0) {
        // Remove captain double cash bonus logic - just sum prizes for remaining players
        potentialTopWinnings = topPrizes.reduce((sum, prize) => sum + prize, 0);
      }
    }
    // Total max cashes = current winnings of eliminated + potential winnings of remaining
    return currentCashes + potentialTopWinnings; 
  }, [playerDataMap, getTopPrizes]); // Dependencies: playerDataMap, getTopPrizes

  // Calculate remaining players based on chip count (memoized)
  const calculateRemainingPlayers = useCallback((team) => {
    let remaining = 0;
    
    // Check Captain
    const captainData = playerDataMap[team.captain];
    const captainChips = captainData?.currentChipCount;
    if (captainChips > 0 || captainChips === null) {
      remaining++;
    }

    // Check Flex Players
    team.flexPlayers.forEach(player => {
      const playerData = playerDataMap[player];
      const playerChips = playerData?.currentChipCount;
      if (playerChips > 0 || playerChips === null) {
        remaining++;
      }
    });
    return remaining;
  }, [playerDataMap]); // Dependency: playerDataMap

  // Pre-process entrants to include place, remaining, and maxCashes for sorting
  const processedEntrants = useMemo(() => {
    // Calculate cashes for each entrant based on player data
    const entrantsWithCalculatedCashes = contest.entrants.map(entrant => {
      let totalCashes = 0;
      let captainCashes = 0;
      
      // Add captain's cashes
      const captainData = playerDataMap[entrant.team.captain];
      if (captainData) {
        captainCashes = captainData.cashedFor || 0;
        totalCashes += captainCashes;
      }
      
      // Add flex players' cashes
      entrant.team.flexPlayers.forEach(player => {
        const playerData = playerDataMap[player];
        if (playerData) {
          totalCashes += playerData.cashedFor || 0;
        }
      });
      
      return {
        ...entrant,
        cashes: totalCashes, // Override the cashes with calculated value
        captainCashes: captainCashes // Store captain's cashes for tiebreaker
      };
    });
    
    // Sort by cashes (descending) and then by captain cashes (descending) for tiebreaker
    const sortedByCashes = [...entrantsWithCalculatedCashes].sort((a, b) => {
      if (b.cashes !== a.cashes) {
        return b.cashes - a.cashes;
      }
      return b.captainCashes - a.captainCashes;
    });
    
    // Process ranks with ties
    let currentRank = 1;
    let currentCashes = null;
    let currentCaptainCashes = null;
    let tieCount = 0;

    return sortedByCashes.map((entrant, index) => {
      // Check if this entrant has the same cashes as the previous one
      const isTiedWithPrevious = currentCashes === entrant.cashes && 
                                currentCaptainCashes === entrant.captainCashes;
      
      if (isTiedWithPrevious) {
        tieCount++;
      } else {
        // New cashes, update rank and reset tie count
        currentRank = index + 1;
        tieCount = 0;
        currentCashes = entrant.cashes;
        currentCaptainCashes = entrant.captainCashes;
      }

      // Look ahead to see if there are more ties
      let lookAheadIndex = index + 1;
      while (lookAheadIndex < sortedByCashes.length) {
        const nextEntrant = sortedByCashes[lookAheadIndex];
        if (nextEntrant.cashes === entrant.cashes && 
            nextEntrant.captainCashes === entrant.captainCashes) {
          tieCount++;
          lookAheadIndex++;
        } else {
          break;
        }
      }

      const processed = {
      ...entrant,
        place: tieCount > 0 ? `T${currentRank}` : currentRank,
      remaining: calculateRemainingPlayers(entrant.team),
      maxCashes: calculateMaxCashes(entrant.team),
      displayName: entrant.entryName || entrant.username,
      teamSalary: entrant.teamSalary
      };
      return processed;
    });
  }, [
    contest.entrants,
    playerDataMap,
    calculateRemainingPlayers,
    calculateMaxCashes
  ]);

  // Apply sorting hook to the processed data
  const { sortedData, requestSort, sortConfig } = useSortedData(
    processedEntrants,
    'cashes' // Initial sort key
  );

  // Determine number of paying places
  const numPayingPlaces = contest.entrantPayouts?.length || 0;

  return (
    <div className="entrants-teams-container-poker">
      <div className="entrants-teams-header">
        <span className="header-title-group">
          <span>Standings</span>
          <span className="header-info-text">(Click team to view)</span>
          {refreshError && (
            <span className="header-error-text">Refresh failed: {refreshError}</span>
          )}
        </span>
        <div className="header-buttons">
          <span className="auto-refresh-text">
            auto-refresh: <span className="countdown-text">{isAutoRefreshEnabled ? `${countdown}s` : 'off'}</span>
          </span>
          <button 
            onClick={onToggleAutoRefresh} 
            className="refresh-button"
            title={isAutoRefreshEnabled ? "Pause auto-refresh" : "Resume auto-refresh"}
          >
            {isAutoRefreshEnabled ? 'on' : 'off'}
          </button>
          <button 
            onClick={onRefreshData} 
            className="refresh-button" 
            title="Manual Refresh"
          >
          {REFRESH_ICON}
        </button>
        </div>
      </div>
      <div className="entrants-teams-table-container">
        <table className="entrants-teams-table">
          <thead>
            <tr>
              <th onClick={() => requestSort('place')}>Rank {getSortIndicator(sortConfig, 'place')}</th>
              <th onClick={() => requestSort('displayName')}>User {getSortIndicator(sortConfig, 'displayName')}</th>
              <th className="show-on-desktop" onClick={() => requestSort('teamSalary')}>Salary {getSortIndicator(sortConfig, 'teamSalary')}</th>
              <th onClick={() => requestSort('cashes')}>Cashes {getSortIndicator(sortConfig, 'cashes')}</th>
              <th onClick={() => requestSort('remaining')}>Rem. {getSortIndicator(sortConfig, 'remaining')}</th>
              <th onClick={() => requestSort('maxCashes')}>Max {getSortIndicator(sortConfig, 'maxCashes')}</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((entrant) => {
              const isCurrentUser = entrant.username?.toLowerCase() === currentUsername?.toLowerCase();
              const teamStatusClass = entrant.remaining > 0 ? 'team-active' : 'team-eliminated';
              const userClass = isCurrentUser ? 'current-user-team' : '';
              const isLastMoneyPlace = numPayingPlaces > 0 && entrant.place === numPayingPlaces;
              const lastMoneyClass = isLastMoneyPlace ? 'last-money-row' : '';

              return (
                <tr
                  key={entrant._id}
                  onClick={() => onTeamSelect(entrant)}
                  className={`${userClass} ${teamStatusClass} ${lastMoneyClass}`.trim()}
                >
                  <td>{entrant.place}</td>
                  <td>
                    {entrant.displayName}
                  </td>
                  <td className="show-on-desktop">${formatNumber(entrant.teamSalary)}</td>
                  <td>${formatNumber(entrant.cashes)}</td>
                  <td>{entrant.remaining}</td>
                  <td>${formatNumber(entrant.maxCashes)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

EntrantsTeamsPokerRunning.propTypes = {
  contest: PropTypes.shape({
    entrants: PropTypes.arrayOf(PropTypes.shape({
      _id: PropTypes.string.isRequired,
      username: PropTypes.string.isRequired,
      entryName: PropTypes.string,
      cashes: PropTypes.number.isRequired,
      team: PropTypes.shape({
        captain: PropTypes.string.isRequired,
        flexPlayers: PropTypes.arrayOf(PropTypes.string).isRequired
      }).isRequired,
      teamSalary: PropTypes.number
    })).isRequired,
    players: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      finish: PropTypes.number, // Still used by calculateMaxCashes
      cashedFor: PropTypes.number.isRequired,
      // Add sportSpecificData for chip count
      sportSpecificData: PropTypes.shape({
        currentChipCount: PropTypes.number
      })
    })).isRequired,
    playerPayouts: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.shape({
        place: PropTypes.number.isRequired,
        prize: PropTypes.number.isRequired,
      })),
      PropTypes.arrayOf(PropTypes.number)
    ]).isRequired,
    entrantPayouts: PropTypes.array,
  }).isRequired,
  currentUsername: PropTypes.string,
  onTeamSelect: PropTypes.func.isRequired,
  onRefreshData: PropTypes.func.isRequired, // Added prop type
  isAutoRefreshEnabled: PropTypes.bool.isRequired,
  onToggleAutoRefresh: PropTypes.func.isRequired,
  refreshError: PropTypes.string,
};

export default EntrantsTeamsPokerRunning;
